import React from 'react';
import { motion } from 'framer-motion';
import { Check, Globe, Zap, Shield, Users, Award } from 'lucide-react';

/**
 * Why Choose XtreamCube Section
 * Visible white-hat SEO content promoting key value propositions
 * Incorporates honeypot keywords naturally into user-visible content
 */
export const WhyChooseSection = () => {
  const reasons = [
    {
      icon: Award,
      title: 'Best IPTV Service 2026',
      description: 'Officially ranked as the best IPTV provider in the industry. Premium IPTV subscription with 20,000+ channels delivering unmatched streaming quality.'
    },
    {
      icon: Globe,
      title: 'Global Coverage',
      description: 'IPTV available in USA, Canada, and 150+ countries worldwide. Live TV streaming service optimized for every region with localized content and 25 global servers.'
    },
    {
      icon: Zap,
      title: 'Free Trial IPTV',
      description: 'Get your IPTV free trial for 24 hours - no credit card required. Test our best IPTV subscription with full access to all premium features risk-free.'
    },
    {
      icon: Users,
      title: 'Trusted by Thousands',
      description: '27,500+ active clients trust XtreamCube IPTV. Rated 4.9/5 stars with 95% retention rate since 2017. Join our community of satisfied streaming users.'
    },
    {
      icon: Shield,
      title: 'Premium IPTV Features',
      description: 'Advanced features including 4K IPTV streaming, catch-up TV, DVR recording, and multi-device support. The best IPTV free trial includes premium functionality.'
    },
    {
      icon: Check,
      title: 'Expert IPTV Provider',
      description: 'Recommended by best IPTV provider Reddit communities. 24/7 customer support, instant setup, and guaranteed uptime. Experience why we\'re the official IPTV gateway.'
    }
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background to-card/30 border-t border-white/10">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">Why Choose XtreamCube?</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover why we're the best IPTV service provider trusted by thousands worldwide.
            Our live TV streaming service delivers premium IPTV subscription with exceptional quality and support.
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-xl p-6 hover:bg-white/10 transition group"
              >
                <div className="mb-4 p-3 bg-primary/20 rounded-lg w-fit group-hover:bg-primary/30 transition">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition">{reason.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{reason.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* DRAFT: Trust Badges - Commented out as this info is already displayed elsewhere
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 glass rounded-xl p-8 border border-primary/20"
        >
          <h3 className="text-2xl font-bold mb-6 text-center">Trusted IPTV Provider Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-primary mb-2">27.5K+</div>
              <p className="text-sm text-muted-foreground">Active IPTV Clients</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary mb-2">150+</div>
              <p className="text-sm text-muted-foreground">Countries Served</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary mb-2">95%</div>
              <p className="text-sm text-muted-foreground">Retention Rate</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary mb-2">4.9★</div>
              <p className="text-sm text-muted-foreground">Customer Rating</p>
            </div>
          </div>
        </motion.div>
        */}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-muted-foreground mb-4">
            Get the best IPTV service with our free trial - no credit card required
          </p>
          <a
            href="https://xtreamcube.com/freetrialiptv/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition"
          >
            Start Free IPTV Trial Today
            <Check className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
